import { useState, type ReactNode } from "react";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { usePlayerPhoto } from "@/features/player-photos/usePlayerPhoto";
import { ApiError } from "@/lib/api";
import type {
  AssignmentStatus,
  AthleteAssignment,
  AthleteDeletionBlocker,
  AthleteRelationship,
  AthleteStatus,
  TeamStatus,
} from "@/lib/types";

/**
 * Display helpers shared by the club-console athlete directory and detail
 * (#908). Pure functions first (unit-tested in `athleteDisplay.test.ts`),
 * then the small presentational pieces both pages render.
 */

/** "Team · Season"; a team without a season reads as its name alone. */
export function assignmentChipLabel(
  assignment: Pick<AthleteAssignment, "team_name" | "season_id" | "season_name">,
): string {
  const team = assignment.team_name || "Team";
  if (!assignment.season_id) return team;
  return `${team} · ${assignment.season_name || assignment.season_id}`;
}

/** The directory's Season filter: the athlete holds an assignment (active
 * or ended) on a team of `seasonId`. */
export function hasSeasonAssignment(
  assignments: readonly Pick<AthleteAssignment, "season_id">[] | undefined,
  seasonId: string,
): boolean {
  return (assignments ?? []).some((a) => a.season_id === seasonId);
}

export function athleteInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "--"
  );
}

/** The search box text after the URL `q` changed to `urlQuery`. The URL
 * holds the TRIMMED draft, so when it already matches the draft (our own
 * debounced write) the draft is kept as typed — overwriting it would eat
 * a trailing space mid-typing ("river " + "s" → "rivers"). Any other URL
 * value (back / forward, a deep link) wins. */
export function searchDraftForUrl(draft: string, urlQuery: string): string {
  return draft.trim() === urlQuery ? draft : urlQuery;
}

/** ISO date (or datetime) → `YYYY-MM-DD`; empty → an em dash. */
export function formatIsoDate(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "—";
}

/** A `1900–2100` whole year, or `null` for an empty field; `undefined`
 * when the text is not an acceptable year. */
export function parseBirthYear(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (!/^\d{4}$/.test(trimmed)) return undefined;
  const year = Number(trimmed);
  return year >= 1900 && year <= 2100 ? year : undefined;
}

/** One team the viewer may roster on, as the roster / transfer pickers
 * list it: built from `GET /api/admin/teams` (club admins) or the
 * `/api/me` teams carrying `roster:manage`. */
export type TeamChoice = {
  id: string;
  name: string;
  seasonName: string | null;
  status: TeamStatus;
};

export function teamChoiceLabel(team: TeamChoice): string {
  return team.seasonName ? `${team.name} · ${team.seasonName}` : team.name;
}

type AssignmentRef = Pick<AthleteAssignment, "team_id" | "assignment_status">;

/** One roster / transfer destination: `rejoin` marks a team where the
 * athlete only holds an ENDED assignment — rostering or transferring there
 * reinstates that row in place instead of creating a new one. */
export type RosterTarget<T> = { team: T; rejoin: boolean };

/** Suffix for a `rejoin` option label ("<Team> · <Season> (rejoin)"). */
export const REJOIN_LABEL_SUFFIX = " (rejoin)";

/** The teams `assignments` can be rostered / transferred onto (#908 D8):
 * a team where the athlete holds an ACTIVE assignment is refused (409)
 * and dropped, an archived team is refused too (new or reinstated) and
 * dropped, and a team with only an ENDED assignment stays as a `rejoin`. */
export function rosterTargets<T extends { id: string; status?: string | null }>(
  teams: readonly T[],
  assignments: readonly AssignmentRef[] | undefined,
): RosterTarget<T>[] {
  const active = new Set<string>();
  const ended = new Set<string>();
  for (const a of assignments ?? []) {
    (a.assignment_status === "active" ? active : ended).add(a.team_id);
  }
  return teams
    .filter((team) => (team.status ?? "active") !== "archived")
    .filter((team) => !active.has(team.id))
    .map((team) => ({ team, rejoin: ended.has(team.id) }));
}

export const ATHLETE_STATUS_LABEL: Record<AthleteStatus, string> = {
  active: "Active",
  archived: "Archived",
  merged: "Merged",
};

/** Which quick actions an athlete row (or the detail header) offers. The
 * rights come from the API (`can_edit`, `can_archive`); the page never
 * guesses them. A merged athlete only opens. Rostering is for active
 * athletes only (an archived one is restored first). Delete is offered only
 * once archived — whether it is then allowed is the server's `deletion`
 * call. */
export type AthleteActionFlags = {
  edit: boolean;
  roster: boolean;
  archive: boolean;
  restore: boolean;
  delete: boolean;
};

export function athleteActionFlags(athlete: {
  status: AthleteStatus;
  can_edit?: boolean;
  can_archive?: boolean;
}): AthleteActionFlags {
  const live = athlete.status !== "merged";
  const admin = live && !!athlete.can_archive;
  return {
    edit: live && !!athlete.can_edit,
    roster: athlete.status === "active",
    archive: admin && athlete.status === "active",
    restore: admin && athlete.status === "archived",
    delete: admin && athlete.status === "archived",
  };
}

/** The blocker list of a structured 409 from `DELETE /api/athletes/{id}`
 * (`code: "athlete_has_data"`), or null for any other error. */
export function deletionBlockersFromError(
  err: unknown,
): AthleteDeletionBlocker[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const detail = err.detail as { code?: unknown; blockers?: unknown } | null;
  if (!detail || typeof detail !== "object") return null;
  if (detail.code !== "athlete_has_data" || !Array.isArray(detail.blockers))
    return null;
  return detail.blockers as AthleteDeletionBlocker[];
}

export const RELATIONSHIP_LABEL: Record<AthleteRelationship, string> = {
  self: "Self",
  guardian: "Guardian",
  family: "Family",
};

/** Field class shared by the directory's dialogs (matches the admin
 * composers: hairline rule, field surface, accent focus ring). */
export const FIELD_CLASS =
  "w-full rounded-control border border-rule bg-field px-2 py-1.5 text-xs text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60";

export const FIELD_LABEL_CLASS =
  "text-[0.6rem] font-display uppercase tracking-wider text-ink-muted";

export function AthleteStatusBadge({ status }: { status: AthleteStatus }) {
  const variant =
    status === "active" ? "good" : status === "merged" ? "outline" : "muted";
  return (
    <Badge
      variant={variant}
      data-testid="athlete-status-badge"
      data-status={status}
    >
      {ATHLETE_STATUS_LABEL[status]}
    </Badge>
  );
}

export function AssignmentStatusBadge({ status }: { status: AssignmentStatus }) {
  return (
    <Badge
      variant={status === "active" ? "good" : "muted"}
      data-assignment-status={status}
    >
      {status === "active" ? "Active" : "Ended"}
    </Badge>
  );
}

/** "Team · Season" pill (the Match Library badge geometry); an ended
 * assignment reads dimmed. */
export function AssignmentChip({ assignment }: { assignment: AthleteAssignment }) {
  const ended = assignment.assignment_status === "ended";
  return (
    <span
      data-testid="athlete-assignment-chip"
      data-assignment-status={assignment.assignment_status}
      title={ended ? "Assignment ended" : undefined}
      className={cn(
        "inline-flex max-w-full items-center rounded-pill bg-elevated px-2.5 py-1 text-[11px] font-medium text-ink-muted",
        ended && "opacity-60",
      )}
    >
      <span className="truncate">{assignmentChipLabel(assignment)}</span>
    </span>
  );
}

/** The athlete's photo (D15). The photo endpoint is keyed by a roster row
 * and the backend coalesces the athlete's photo onto every assignment, so
 * any assignment's `player_id` resolves it; no assignment ⇒ initials. */
export function AthleteAvatar({
  name,
  playerId,
  photoFilename,
  className,
}: {
  name: string;
  playerId: string | null;
  photoFilename: string | null | undefined;
  className?: string;
}) {
  const url = usePlayerPhoto("player", playerId, photoFilename ?? null);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return (
    <span
      data-testid="admin-athlete-avatar"
      aria-hidden="true"
      className={cn(
        "relative inline-flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-rule bg-field font-display text-base font-semibold text-ink",
        className,
      )}
    >
      {url && url !== failedUrl ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
          onError={() => setFailedUrl(url)}
        />
      ) : (
        <span>{athleteInitials(name)}</span>
      )}
    </span>
  );
}

/** Section heading for the detail page's panels — the People page's
 * eyebrow + title header with the section action on the right. */
export function AthletePanelHeading({
  id,
  eyebrow,
  title,
  description,
  action,
}: {
  id: string;
  eyebrow: string;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
      <div className="min-w-0">
        <p className="people-panel-eyebrow">{eyebrow}</p>
        <h3 id={id} className="people-panel-title">
          {title}
        </h3>
        {description ? (
          <p className="mt-1 max-w-3xl text-xs text-ink-muted">{description}</p>
        ) : null}
      </div>
      {action ? <div className="flex items-center gap-2">{action}</div> : null}
    </header>
  );
}
